import { Content, ContentPreview } from "@turtlewave/types";
import { z } from "zod";

import { CompilationMinimalSchema } from "../compilations/schemas";

import { ContentValidationError } from "./errors";

/**
 * Zod schema for PublishDate
 */
export const PublishDateSchema = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12).nullable(),
  day: z.number().int().min(1).max(31).nullable(),
});

/**
 * Zod schema for Media
 */
export const ContentImageSchema = z.object({
  url: z.string().url(),
});

/**
 * Zod schema for SupplementalMaterial
 */
export const SupplementalMaterialSchema = z.object({
  embed_url: z.string().url().nullable(),
  // Placeholder until content is expressed via string data in the future.
  content: z.null(),
});

/**
 * Zod schema for full ContentData
 */
export const ContentDataSchema = z.object({
  content_id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  tagline: z.string().nullable(),
  publish_date: PublishDateSchema,
  medium: z.array(z.string()),
  dimensions: z.string(),
  purchase_link: z.string().url().nullable(),
  price: z.number().nullable(),
  display_media: z.array(ContentImageSchema),
  auxiliary_media: z.array(ContentImageSchema),
  preview_url: z.string().url().nullable(),
  preview_width: z.number(),
  preview_height: z.number(),
  supplemental_material: SupplementalMaterialSchema.nullable(),
  compilations: z.array(CompilationMinimalSchema),
});

/**
 * Zod schema for ContentPreviewData
 */
export const ContentPreviewDataSchema = z.object({
  content_id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  publish_date: PublishDateSchema,
  price: z.number().nullable(),
  medium: z.array(z.string()),
  preview_url: z.string().url().nullable(),
  preview_width: z.number(),
  preview_height: z.number(),
  compilations: z.array(CompilationMinimalSchema),
});

/**
 * Zod schema for array of ContentData
 */
export const ContentArraySchema = z.array(ContentDataSchema);

/**
 * Zod schema for array of ContentPreviewData
 */
export const ContentPreviewArraySchema = z.array(ContentPreviewDataSchema);

/**
 * Validates an array of content data and throws ContentValidationError on failure
 * @param data - Unknown data to validate
 * @returns Validated array of ContentData
 * @throws ContentValidationError if validation fails
 */
export function validateContentArray(data: unknown): ContentPreview[] {
  try {
    return ContentPreviewArraySchema.parse(data);
  } catch (error) {
    throw new ContentValidationError("Content data validation failed", error);
  }
}

/**
 * Validates a single content data object and throws ContentValidationError on failure
 * @param data - Unknown data to validate
 * @returns Validated ContentData
 * @throws ContentValidationError if validation fails
 */
export function validateContentData(data: unknown): Content {
  try {
    return ContentDataSchema.parse(data);
  } catch (error) {
    throw new ContentValidationError("Content data validation failed", error);
  }
}
